/**
 * Icon geometry — 28 icons, transcribed rather than imported.
 *
 * ATTRIBUTION: the 25 stroke icons are Lucide geometry (https://lucide.dev),
 * ISC licence, © Lucide Contributors. The paths were transcribed by hand from the
 * design prototype, which had itself transcribed them from Lucide. `lucide-react`
 * is deliberately NOT a dependency: it is not on the permitted stack list, and it
 * would ship a tree-shaking liability for 28 paths that are all known at build
 * time. The three brand marks (`facebook`, `x`, `linkedin`) are filled single
 * paths and are not Lucide's.
 *
 * Geometry lives in SVG **presentation attributes** (`d`, `cx`, `cy`, `r`, `x`,
 * `y`, `width`, `height`, `rx`). Those are not CSS inline styles and CSP has
 * nothing to say about them — unlike a `style` attribute, which `style-src 'self'`
 * would block outright. Colour never appears here: it comes from `currentColor`
 * plus a class.
 */

export interface IconPathPart {
  readonly tag: 'path'
  readonly d: string
}

export interface IconCirclePart {
  readonly tag: 'circle'
  readonly cx: number
  readonly cy: number
  readonly r: number
}

export interface IconRectPart {
  readonly tag: 'rect'
  readonly x: number
  readonly y: number
  readonly width: number
  readonly height: number
  readonly rx: number
}

export type IconPart = IconPathPart | IconCirclePart | IconRectPart

export interface IconSpec {
  /**
   * `stroke` icons are outlined at 1.6 with round caps; `fill` icons are solid
   * silhouettes. A per-icon flag rather than a second component — the difference
   * is two attributes, not two behaviours.
   */
  readonly variant: 'stroke' | 'fill'
  readonly parts: readonly IconPart[]
}

function path(d: string): IconPathPart {
  return { tag: 'path', d }
}

function circle(cx: number, cy: number, r: number): IconCirclePart {
  return { tag: 'circle', cx, cy, r }
}

export const ICON_PATHS = Object.freeze({
  // --- Poem icons (9) --------------------------------------------------------
  bird: {
    variant: 'stroke',
    parts: [
      path('M16 7h.01'),
      path('M3.4 18H12a8 8 0 0 0 8-8V7a4 4 0 0 0-7.28-2.3L2 20'),
      path('m20 7 2 .5-2 .5'),
      path('M10 18v3'),
      path('M14 17.75V21'),
      path('M7 18a6 6 0 0 0 3.84-10.61'),
    ],
  },
  chuva: {
    variant: 'stroke',
    parts: [
      path('M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242'),
      path('M16 14v6'),
      path('M8 14v6'),
      path('M12 16v6'),
    ],
  },
  feather: {
    variant: 'stroke',
    parts: [
      path(
        'M12.67 19a2 2 0 0 0 1.416-.588l6.154-6.172a6 6 0 0 0-8.49-8.49L5.593 9.92A2 2 0 0 0 5 11.34V18a1 1 0 0 0 1 1z',
      ),
      path('M16 8 2 22'),
      path('M17.5 15H9'),
    ],
  },
  janela: {
    variant: 'stroke',
    parts: [
      { tag: 'rect', x: 3, y: 3, width: 18, height: 18, rx: 2 },
      path('M3 12h18'),
      path('M12 3v18'),
    ],
  },
  leaf: {
    variant: 'stroke',
    parts: [
      path('M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z'),
      path('M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12'),
    ],
  },
  moon: {
    variant: 'stroke',
    parts: [path('M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z')],
  },
  smile: {
    variant: 'stroke',
    parts: [circle(12, 12, 10), path('M8 14s1.5 2 4 2 4-2 4-2'), path('M9 9h.01'), path('M15 9h.01')],
  },
  sun: {
    variant: 'stroke',
    parts: [
      circle(12, 12, 4),
      path('M12 2v2'),
      path('M12 20v2'),
      path('m4.93 4.93 1.41 1.41'),
      path('m17.66 17.66 1.41 1.41'),
      path('M2 12h2'),
      path('M20 12h2'),
      path('m6.34 17.66-1.41 1.41'),
      path('m19.07 4.93-1.41 1.41'),
    ],
  },
  wind: {
    variant: 'stroke',
    parts: [
      path('M17.7 7.7a2.5 2.5 0 1 1 1.8 4.3H2'),
      path('M9.6 4.6A2 2 0 1 1 11 8H2'),
      path('M12.6 19.4A2 2 0 1 0 14 16H2'),
    ],
  },

  // --- UI icons (19) ---------------------------------------------------------
  'arrow-right': {
    variant: 'stroke',
    parts: [path('M5 12h14'), path('m12 5 7 7-7 7')],
  },
  /**
   * The ebook launcher. Lucide `book-open`.
   */
  'book-open': {
    variant: 'stroke',
    parts: [
      path('M12 7v14'),
      path(
        'M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z',
      ),
    ],
  },
  camera: {
    variant: 'stroke',
    parts: [
      path('M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z'),
      circle(12, 13, 3),
    ],
  },
  cat: {
    variant: 'stroke',
    parts: [
      path(
        'M12 5c.67 0 1.35.09 2 .26 1.78-2 5.03-2.84 6.42-2.26 1.4.58-.42 7-.42 7 .57 1.07 1 2.24 1 3.44C21 17.9 16.97 21 12 21s-9-3-9-7.56c0-1.25.5-2.4 1-3.44 0 0-1.89-6.42-.5-7 1.39-.58 4.72.23 6.5 2.23A9.04 9.04 0 0 1 12 5Z',
      ),
      path('M8 14v.5'),
      path('M16 14v.5'),
      path('M11.25 16.25h1.5L12 17l-.75-.75Z'),
    ],
  },
  'chevron-left': {
    variant: 'stroke',
    parts: [path('m15 18-6-6 6-6')],
  },
  /**
   * Lucide `x`, and NOT the `x` key below — that one is the brand mark, a filled
   * silhouette of a very different shape. Two icons genuinely called "x" is the
   * kind of collision that ends in the wrong one being rendered, so the one that
   * means "dismiss this" is named for what it does.
   */
  close: {
    variant: 'stroke',
    parts: [path('M18 6 6 18'), path('m6 6 12 12')],
  },
  'chevron-right': {
    variant: 'stroke',
    parts: [path('m9 18 6-6-6-6')],
  },
  flower: {
    variant: 'stroke',
    parts: [
      circle(12, 8, 2),
      path('M12 10v12'),
      path('M12 22c4.2 0 7-1.667 7-5-4.2 0-7 1.667-7 5z'),
      path('M12 22c-4.2 0-7-1.667-7-5 4.2 0 7 1.667 7 5z'),
    ],
  },
  home: {
    variant: 'stroke',
    parts: [
      path('M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8'),
      path(
        'M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z',
      ),
    ],
  },
  /**
   * The two faces of the poem route's illustration switch. Lucide `image` and
   * `image-off`.
   *
   * A PAIR RATHER THAN ONE ICON AND A PRESSED PILL. Each glyph depicts what is
   * true now, which is the one state signal that survives both palettes and
   * survives the label being clipped to nothing below 36em. `poem-page.tsx` has
   * the full reasoning, including why the `toggle` variant is wrong there.
   *
   * Lucide's `ry` is dropped from the rect — `IconRectPart` carries `rx` alone,
   * and an SVG rect with `rx` and no `ry` takes `ry` from it, so the corner is
   * unchanged. Lucide's three `<line>` elements become `path`s for the same
   * reason: the primitive draws three shapes, and a straight run is a two-point
   * path.
   */
  image: {
    variant: 'stroke',
    parts: [
      { tag: 'rect', x: 3, y: 3, width: 18, height: 18, rx: 2 },
      circle(9, 9, 2),
      path('m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21'),
    ],
  },
  'image-off': {
    variant: 'stroke',
    parts: [
      path('M10.41 10.41a2 2 0 1 1-2.83-2.83'),
      path('M13.5 13.5 6 21'),
      path('M18 12 21 15'),
      path('M3.59 3.59A1.99 1.99 0 0 0 3 5v14a2 2 0 0 0 2 2h14c.55 0 1.052-.22 1.41-.59'),
      path('M21 15V5a2 2 0 0 0-2-2H9'),
      path('M2 2 22 22'),
    ],
  },
  list: {
    variant: 'stroke',
    parts: [path('M3 6h18'), path('M3 12h18'), path('M3 18h18')],
  },
  'map-pin': {
    variant: 'stroke',
    parts: [
      path(
        'M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0',
      ),
      circle(12, 10, 3),
    ],
  },
  /**
   * The two transport controls of the ebook reader. Lucide `pause` and `play`.
   *
   * `play` is Lucide's polygon re-expressed as a closed path: the primitive draws
   * `path`, `circle` and `rect` and nothing else, and a triangle that only exists
   * once does not earn a fourth part type.
   */
  pause: {
    variant: 'stroke',
    parts: [
      { tag: 'rect', x: 14, y: 4, width: 4, height: 16, rx: 1 },
      { tag: 'rect', x: 6, y: 4, width: 4, height: 16, rx: 1 },
    ],
  },
  play: {
    variant: 'stroke',
    parts: [path('M6 3 20 12 6 21Z')],
  },
  share: {
    variant: 'stroke',
    parts: [
      circle(18, 5, 3),
      circle(6, 12, 3),
      circle(18, 19, 3),
      path('m8.6 13.5 6.8 4'),
      path('m15.4 6.5-6.8 4'),
    ],
  },

  // --- Brand marks (3), filled silhouettes -----------------------------------
  facebook: {
    variant: 'fill',
    parts: [
      path(
        'M13.5 22v-8h2.8l.4-3.2h-3.2V8.7c0-.9.3-1.6 1.6-1.6h1.7V4.2c-.3 0-1.3-.1-2.5-.1-2.5 0-4.2 1.5-4.2 4.3v2.4H7.3V14h2.8v8z',
      ),
    ],
  },
  linkedin: {
    variant: 'fill',
    parts: [
      path(
        'M6.9 8.6H4V20h2.9zM5.4 3.6a1.7 1.7 0 1 0 0 3.4 1.7 1.7 0 0 0 0-3.4M20 13.7c0-3-1.6-4.4-3.8-4.4-1.7 0-2.6.9-3 1.6V8.6H10.3c0 .8 0 11.4 0 11.4h2.9v-6.2c0-.3 0-.6.1-.9.3-.6.8-1.3 1.8-1.3 1.3 0 1.9.9 1.9 2.4V20H20z',
      ),
    ],
  },
  x: {
    variant: 'fill',
    parts: [
      path('M17.5 3h3.2l-7 8 7.3 10h-5.6l-4.4-6-5 6H2.8l7.3-8.6L3 3h5.7l4.1 5.6zm-1.1 16h1.8L7.5 4.9H5.6z'),
    ],
  },
} as const satisfies Record<string, IconSpec>)

/** A union over the record's keys — there is no string `name` anywhere. */
export type IconName = keyof typeof ICON_PATHS

export const ICON_NAMES = Object.freeze(Object.keys(ICON_PATHS) as readonly IconName[])
